import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from '@emotion/styled';

import { outputIfNoVariables } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { headers, body, richTextStyles } from '../../styles/type';

import Button from '../buttons/Button';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}

  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(40)};

  ${media('>small')} {
    grid-template-columns: 1fr 1fr;
  }

  ${media('>smedium')} {
    gap: ${rem(80)};
  }

  ${media('>medium')} {
    gap: ${rem(120)};
  }
`;

const ImageContainer = styled.div`
  ${media('>small')} {
    order: ${({ imagePosition }) => (imagePosition === 'Left' ? -1 : 1)};
  }
`;

const Image = styled(GatsbyImage)`
  border-radius: ${rem(10)};
`;

const TextContent = styled.div`
  position: relative;

  ${media('>small')} {
    margin: auto;
  }

  ${Button} {
    margin-top: ${rem(32)};
  }
`;

const Headline = styled.h2`
  ${headers.h3}
  color: ${colors.blue['800']};
`;

const Text = styled.div`
  ${body.regular}
  ${richTextStyles}
  margin-top: ${rem(20)};
  color: ${colors.gray['600']};
`;

const TextMedia = ({
  image,
  imagePosition = 'Left',
  headline,
  text,
  ctaText,
  ctaLink,
  backgroundColor
}) => (
  <Container backgroundColor={backgroundColor}>
    <ContentWrapper>
      <ImageContainer imagePosition={imagePosition}>
        {image && (
          <Image
            image={getImage(image)}
            alt={image.description}
          />
        )}
      </ImageContainer>
      <TextContent imagePosition={imagePosition}>
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}
        {text && (
          <Text>
            <TextRenderer text={text} richText />
          </Text>
        )}
        {ctaText && ctaLink && (
          <Button
            to={ctaLink}
            aria-label={outputIfNoVariables(`${headline} — ${ctaText}`)}
          >
            <TextRenderer text={ctaText} />
          </Button>
        )}
      </TextContent>
    </ContentWrapper>
  </Container>
);

TextMedia.propTypes = {
  image: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  imagePosition: PropTypes.oneOf(['Left', 'Right']).isRequired,
  headline: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment TextMediaFragment on ContentfulTextMedia {
    id
    __typename
    image {
      gatsbyImageData(width: 590, height: 394, quality: 75, resizingBehavior: FILL)
      description
    }
    imagePosition
    headline
    text {
      raw
    }
    ctaText
    ctaLink
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default TextMedia;
